import React, { useState } from 'react';
import { X, CheckCircle, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import {
  signUp,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  signInWithRedirect,
} from 'aws-amplify/auth';

const SignUpModal = ({ isOpen, onClose }) => {
  const [view, setView] = useState('signup'); // signup | confirm | success | forgot-password | reset-password
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signUp({
        username: email.trim().toLowerCase(),
        password,
        options: { userAttributes: { email: email.trim().toLowerCase() } },
      });
      setView('confirm');
    } catch (err) {
      setError(err.message || 'Sign up failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmSignUp({ username: email.trim().toLowerCase(), confirmationCode: code });
      setView('success');
    } catch (err) {
      setError(err.message || 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    try {
      await resendSignUpCode({ username: email.trim().toLowerCase() });
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to resend code.');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await resetPassword({ username: email.trim().toLowerCase() });
      setView('reset-password');
    } catch (err) {
      setError(err.message || 'Failed to send reset code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmResetPassword({
        username: email.trim().toLowerCase(),
        confirmationCode: code,
        newPassword,
      });
      setView('success');
    } catch (err) {
      setError(err.message || 'Password reset failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSignIn = async (provider) => {
    try {
      if (provider === 'Google') {
        await signInWithRedirect({ provider: 'Google' });
      } else {
        await signInWithRedirect({ provider: { custom: 'LinkedIn' } });
      }
    } catch (err) {
      setError(err.message || `${provider} sign in failed.`);
    }
  };

  const inputClass = (hasError) =>
    `w-full px-4 py-3 border text-black placeholder:text-zinc-400 text-xs font-mono bg-transparent focus:outline-none focus:border-[#4D4D4D] transition-colors ${
      hasError ? 'border-red-500' : 'border-zinc-300'
    }`;
  const labelClass = 'font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1 block';
  const btnPrimary =
    'w-full py-3 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] hover:bg-black transition-all disabled:opacity-50 flex items-center justify-center gap-2';
  const btnOutline =
    'w-full py-3 font-mono text-xs uppercase tracking-[0.15em] font-medium border border-zinc-300 text-black hover:border-[#4D4D4D] transition-all flex items-center justify-center gap-2';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white border border-zinc-300 p-8 max-w-md w-full relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-400 hover:text-black transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* ── Sign Up View ── */}
        {view === 'signup' && (
          <>
            <h3 className="font-sans text-xl font-bold text-black mb-2">Create Account</h3>
            <p className="text-sm text-zinc-600 font-sans mb-6">
              Sign up to manage your cross-border cashflows end to end.
            </p>

            {/* Social buttons */}
            <div className="space-y-3 mb-6">
              <button onClick={() => handleSocialSignIn('Google')} className={btnOutline}>
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Continue with Google
              </button>
              <button onClick={() => handleSocialSignIn('LinkedIn')} className={btnOutline}>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#0A66C2">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                Continue with LinkedIn
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-zinc-200" />
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">
                or continue with email
              </span>
              <div className="flex-1 h-px bg-zinc-200" />
            </div>

            <form onSubmit={handleSignUp} className="space-y-4">
              <div>
                <label className={labelClass}>Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={inputClass(!!error)}
                  disabled={loading}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 8 chars, upper, lower, number"
                    className={inputClass(!!error)}
                    disabled={loading}
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-black"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {error && (
                <p className="text-xs font-mono text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  {error}
                </p>
              )}
              <button type="submit" disabled={loading} className={btnPrimary}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Account'}
              </button>
            </form>

            <p className="mt-4 text-center text-xs font-mono text-zinc-500">
              Already have an account?{' '}
              <a href="/login" className="text-black underline hover:no-underline">
                Sign In
              </a>
            </p>
          </>
        )}

        {/* ── Confirm View ── */}
        {view === 'confirm' && (
          <>
            <h3 className="font-sans text-xl font-bold text-black mb-2">Verify Your Email</h3>
            <p className="text-sm text-zinc-600 font-sans mb-6">
              We sent a 6-digit code to <span className="font-medium text-black">{email}</span>.
            </p>
            <form onSubmit={handleConfirm} className="space-y-4">
              <div>
                <label className={labelClass}>Verification Code</label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className={`${inputClass(!!error)} text-center text-lg tracking-[0.5em]`}
                  disabled={loading}
                  required
                  maxLength={6}
                />
              </div>
              {error && (
                <p className="text-xs font-mono text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  {error}
                </p>
              )}
              <button type="submit" disabled={loading || code.length < 6} className={btnPrimary}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
              </button>
            </form>
            <p className="mt-4 text-center text-xs font-mono text-zinc-500">
              Didn't receive the code?{' '}
              <button onClick={handleResend} className="text-black underline hover:no-underline">
                Resend
              </button>
            </p>
          </>
        )}

        {/* ── Success View ── */}
        {view === 'success' && (
          <div className="text-center py-4">
            <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
            <h3 className="font-sans text-xl font-bold text-black mb-2">Account Created</h3>
            <p className="text-sm text-zinc-600 font-sans mb-6">
              Your account has been verified. You can now sign in.
            </p>
            <a
              href="/login"
              className="inline-block px-8 py-3 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] hover:bg-black transition-all"
            >
              Sign In
            </a>
            <button
              onClick={onClose}
              className="mt-4 block mx-auto font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 hover:text-black transition-colors"
            >
              Close
            </button>
          </div>
        )}

        {/* ── Forgot Password View ── */}
        {view === 'forgot-password' && (
          <>
            <h3 className="font-sans text-xl font-bold text-black mb-2">Reset Password</h3>
            <p className="text-sm text-zinc-600 font-sans mb-6">
              Enter your email and we'll send you a reset code.
            </p>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className={labelClass}>Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={inputClass(!!error)}
                  disabled={loading}
                  required
                />
              </div>
              {error && (
                <p className="text-xs font-mono text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  {error}
                </p>
              )}
              <button type="submit" disabled={loading} className={btnPrimary}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Reset Code'}
              </button>
            </form>
            <p className="mt-4 text-center text-xs font-mono text-zinc-500">
              <button
                onClick={() => { setError(''); setView('signup'); }}
                className="text-black underline hover:no-underline"
              >
                Back to Sign Up
              </button>
            </p>
          </>
        )}

        {/* ── Reset Password View ── */}
        {view === 'reset-password' && (
          <>
            <h3 className="font-sans text-xl font-bold text-black mb-2">Set New Password</h3>
            <p className="text-sm text-zinc-600 font-sans mb-6">
              Enter the code sent to <span className="font-medium text-black">{email}</span> and your new password.
            </p>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className={labelClass}>Reset Code</label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className={`${inputClass(!!error)} text-center text-lg tracking-[0.5em]`}
                  disabled={loading}
                  required
                  maxLength={6}
                />
              </div>
              <div>
                <label className={labelClass}>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 8 chars, upper, lower, number"
                  className={inputClass(!!error)}
                  disabled={loading}
                  required
                  minLength={8}
                />
              </div>
              {error && (
                <p className="text-xs font-mono text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  {error}
                </p>
              )}
              <button type="submit" disabled={loading || code.length < 6} className={btnPrimary}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset Password'}
              </button>
            </form>
          </>
        )}

        {/* Forgot password link — shown on signup view */}
        {view === 'signup' && (
          <p className="mt-2 text-center text-xs font-mono text-zinc-500">
            <button
              onClick={() => { setError(''); setView('forgot-password'); }}
              className="text-zinc-500 underline hover:text-black hover:no-underline"
            >
              Forgot password?
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

export default SignUpModal;
